import React from 'react';
import { Card, pnlCls, money } from './LearningParts';

/** Karar türü karnesi (tür başına isabet / kâr / stop / hedef / not). */
export function KindsGrid({ kinds, minTrades }) {
    if (!kinds?.length) return <Card title="Karar türleri" testId="learn-kinds"><div className="text-[11px] text-slate-400">Henüz kapanan işlem yok — ilk kapanışlarla tür istatistikleri başlar.</div></Card>;
    return (
        <Card title="Karar türleri karnesi" testId="learn-kinds" right={<span className="text-[10px] text-slate-500">kural için tür başına {minTrades} kapanış</span>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {kinds.map((k) => (
                    <div key={k.kind} className={`rounded-md border px-2.5 py-1.5 ${k.paused_until ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-700/70 bg-slate-950/40'}`} data-testid={`learn-kind-${k.kind}`}>
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-xs font-bold text-slate-100">{k.label}</span>
                            <span className={`font-mono text-xs font-bold ${pnlCls(k.pnl_usd)}`}>{money(k.pnl_usd)}</span>
                        </div>
                        <div className="h-1 rounded-full bg-slate-800 overflow-hidden my-1"><div className={`h-full ${k.ready ? 'bg-violet-400' : 'bg-slate-500'}`} style={{ width: `${Math.min(100, (k.n / minTrades) * 100)}%` }} /></div>
                        <div className="text-[10px] text-slate-400">{k.n} işlem · %{k.win_rate} isabet · stop %{k.stop_rate} · hedef %{k.tp_rate} · ort. {k.avg_hold_h} sa · ort. kâr {Number(k.avg_win).toFixed(1)}$ / zarar {Number(k.avg_loss).toFixed(1)}$</div>
                        <div className="text-[10px] text-violet-200">{k.note}</div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

function Row({ label, s, testId }) {
    return (
        <div className="flex items-center gap-2 text-[11px]" data-testid={testId}>
            <span className="w-14 shrink-0 text-slate-300 font-mono">{label}</span>
            <div className="flex-1 h-2 rounded bg-slate-800 overflow-hidden relative">
                {s.n > 0 && <div className={`absolute inset-y-0 ${s.pnl_usd >= 0 ? 'left-1/2 bg-emerald-400/70' : 'right-1/2 bg-rose-400/70'}`} style={{ width: `${Math.min(50, Math.abs(s.pnl_usd) / 2)}%` }} />}
                <div className="absolute inset-y-0 left-1/2 w-px bg-slate-600" />
            </div>
            <span className={`w-16 text-right font-mono ${pnlCls(s.pnl_usd)}`}>{s.n ? money(s.pnl_usd) : '—'}</span>
            <span className="w-24 text-right text-slate-500">{s.n ? `${s.n} işlem · %${s.win_rate}` : 'veri yok'}</span>
        </div>
    );
}

/** LONG/SHORT ve saat dilimi karnesi. */
export function SideHoursCard({ side, hours, reliable }) {
    return (
        <Card title="Yön ve saat karnesi" testId="learn-side-hours" right={!reliable && <span className="text-[10px] text-amber-300">az veri — 20 kapanışta güvenilir</span>}>
            <div className="space-y-1">
                {side && Object.entries(side).map(([k, s]) => <Row key={k} label={k} s={s} testId={`learn-side-${k}`} />)}
            </div>
            <div className="border-t border-slate-800 pt-1.5 space-y-1">
                {(hours || []).map((h) => <Row key={h.label} label={h.label} s={h} testId={`learn-hour-${h.label.replace('–', '-')}`} />)}
            </div>
            <div className="text-[10px] text-slate-500">Saat: Türkiye saati, işlemin açıldığı dilim.</div>
        </Card>
    );
}

const REASON_TR = { stop: 'stop', tp: 'hedef / kilit', time: 'zaman / sönme', manual: 'elle', other: 'diğer' };

/** Kâr-zarar dengesi: ortalama kâr ÷ ortalama zarar, kâr faktörü, kapanış nedeni dağılımı. */
export function BalanceCard({ balance, reliable }) {
    if (!balance) return null;
    const total = Object.values(balance.reasons || {}).reduce((a, b) => a + b, 0) || 1;
    const rr = balance.avg_loss ? Math.abs(balance.avg_win / balance.avg_loss) : null;
    return (
        <Card title="Kâr-zarar dengesi" testId="learn-balance" right={!reliable && <span className="text-[10px] text-amber-300">öneriler 50 kapanışta güvenilir</span>}>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2 py-1.5"><div className="text-[10px] text-slate-500">Brüt kâr</div><div className="font-mono font-bold text-emerald-300">+{Number(balance.gross_profit).toFixed(2)}$</div></div>
                <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2 py-1.5"><div className="text-[10px] text-slate-500">Brüt zarar</div><div className="font-mono font-bold text-rose-300">{Number(balance.gross_loss).toFixed(2)}$</div></div>
                <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2 py-1.5"><div className="text-[10px] text-slate-500">Kâr faktörü</div><div className={`font-mono font-bold ${balance.profit_factor >= 1 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="learn-balance-pf">{balance.profit_factor}</div></div>
            </div>
            {rr != null && (
                <div className="text-[11px] text-slate-300" data-testid="learn-balance-rr">
                    Ortalama kâr <b className="text-emerald-300">{Number(balance.avg_win).toFixed(2)}$</b> · ortalama zarar <b className="text-rose-300">{Number(balance.avg_loss).toFixed(2)}$</b> → oran <b className={rr >= 1 ? 'text-emerald-300' : 'text-amber-300'}>{rr.toFixed(2)}</b>.
                    {rr < 1 ? ` Zararlar kârlardan büyük: başabaş için %${Math.round(100 / (1 + rr))} isabet gerekir.` : ' Kazanç zarardan büyük — isabet %50 altında bile kâr edilebilir.'}
                </div>
            )}
            <div className="flex h-2 rounded overflow-hidden" data-testid="learn-balance-reasons">
                {Object.entries(balance.reasons || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                    <div key={k} title={`${REASON_TR[k]}: ${n}`} className={`${k === 'stop' ? 'bg-rose-400/70' : k === 'tp' ? 'bg-emerald-400/70' : k === 'time' ? 'bg-sky-400/70' : 'bg-slate-500/70'}`} style={{ width: `${(n / total) * 100}%` }} />
                ))}
            </div>
            <div className="text-[10px] text-slate-400">Kapanış nedenleri: {Object.entries(balance.reasons || {}).filter(([, n]) => n > 0).map(([k, n]) => `${REASON_TR[k]} ${n}`).join(' · ') || '—'}</div>
        </Card>
    );
}

/** Coin karnesi: en çok kazandıran / kaybettiren coinler. */
export function CoinsCard({ coins, reliable, onSelect }) {
    if (!coins) return null;
    const List = ({ items, testId }) => (
        <div className="space-y-1" data-testid={testId}>
            {items.length === 0 && <div className="text-[10px] text-slate-500">—</div>}
            {items.map((c) => (
                <button key={c.symbol} type="button" onClick={() => onSelect && onSelect(c.symbol)} className="w-full flex items-center justify-between gap-2 text-[11px] rounded px-1.5 py-0.5 hover:bg-slate-800/70 text-left" data-testid={`learn-coin-${c.symbol}`}>
                    <span className="font-mono text-slate-100">{c.symbol.replace('USDT', '')}</span>
                    <span className="text-slate-500">{c.n} işlem · %{c.win_rate}</span>
                    <span className={`font-mono font-bold ${pnlCls(c.pnl_usd)}`}>{money(c.pnl_usd)}</span>
                </button>
            ))}
        </div>
    );
    return (
        <Card title={`Coin karnesi · ${coins.count} coin`} testId="learn-coins" right={!reliable && <span className="text-[10px] text-amber-300">50 kapanışta güvenilir</span>}>
            <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] text-emerald-300 font-semibold mb-1">En çok kazandıran</div><List items={coins.best} testId="learn-coins-best" /></div>
                <div><div className="text-[10px] text-rose-300 font-semibold mb-1">En çok kaybettiren</div><List items={coins.worst} testId="learn-coins-worst" /></div>
            </div>
        </Card>
    );
}

const EVENT_ICON = { level_up: '⬆️', grade_change: '🎓', kind_pause: '⏸', kind_resume: '▶️', coin_skip: '⛔', profile_apply: '✅', profile_remove: '➖', profile_revert: '↩️' };

/** Öğrenme geçmişi: seviye atlama, not değişimi, tür durdurma/açma, coin atlama. */
export function HistoryList({ history, skipped }) {
    return (
        <Card title="Değişiklik geçmişi" testId="learn-history">
            {skipped?.length > 0 && (
                <div className="text-[10px] text-amber-200" data-testid="learn-skipped">
                    Şu an atlanan coinler: {skipped.map((x) => `${x.symbol.replace('USDT', '')} (${Number(x.losses).toFixed(2)}$, ${new Date(x.until * 1000).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}'e kadar)`).join(' · ')}
                </div>
            )}
            {(!history || history.length === 0) && <div className="text-[11px] text-slate-400">Henüz bir kural değişikliği olmadı — seviye atlayınca, not değişince, bir tür durdurulunca/açılınca ya da bir coin atlanınca burada görünür.</div>}
            <ul className="space-y-1">
                {(history || []).map((h, i) => (
                    <li key={`${h.ts}-${i}`} className="flex items-start gap-2 text-[11px]" data-testid={`learn-history-${i}`}>
                        <span className="shrink-0">{EVENT_ICON[h.type] || '•'}</span>
                        <span className="font-mono text-[10px] text-slate-500 shrink-0 w-24">{new Date(h.ts * 1000).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="text-slate-200">{h.text}</span>
                    </li>
                ))}
            </ul>
        </Card>
    );
}
